import React, { HTMLProps, RefObject, useCallback, useEffect, useRef, useState } from 'react'

interface CarouselProps extends HTMLProps<HTMLDivElement> {
  items: string[]
  reverse?: boolean
  boundaries: { left: number; right: number }
  isVisible: boolean
  scrollRef: RefObject<HTMLDivElement>
}

const Carousel = ({
    reverse = false,
    items,
    boundaries,
    isVisible,
    id,
    scrollRef,
}: CarouselProps) => {
    const [currentItems, setCurrentItems] = useState(items)
    const ref = useRef<HTMLDivElement>(null)
    const transformRef = useRef<HTMLDivElement>(null)
    const [initialXPosition, setInitialXPosition] = useState(0)

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.addEventListener('mouseleave', onMouseUp)
        }
    }, [scrollRef])

    const onMouseDown = () => {
        if (ref.current) {
            ref.current.style.cursor = 'grabbing'
            ref.current.style.userSelect = 'none'
            setInitialXPosition(0)
            ref.current.addEventListener('mousemove', onMouseMove)
            ref.current.addEventListener('mouseup', onMouseUp)
        }
    }

    const onMouseUp = () => {
        if (ref.current) {
            ref.current.style.cursor = 'auto'
            ref.current.removeEventListener('mousemove', onMouseMove)
        }
    }

    const onMouseMove = useCallback(
        (e: { movementX: number }) => {
            const dx = initialXPosition - e.movementX
            const carousels = document.querySelectorAll('.carousel')

            carousels.forEach((carousel) => {
                const isReverse = carousel.getAttribute('dir') === 'rtl'
                const left = dx * (isReverse ? -1 : 1)
                carousel.scrollBy(left, 0)
            })
        },
        [initialXPosition],
    )

    useEffect(() => {
        if (ref.current) {
            const calculateScrollX = () => {
                const left = (ref.current?.scrollLeft ?? 0) * (reverse ? -1 : 1)
                const carousels = document.querySelectorAll('.carousel')

                carousels.forEach((carousel) => {
                    if (carousel.id !== id) {
                        const isReverse = carousel.getAttribute('dir') === 'rtl'
                        carousel.scrollTo(left * (isReverse ? -1 : 1), 0)
                    }
                })
            }
            ref.current.addEventListener('mouseenter', () => {
        ref.current?.addEventListener('scroll', calculateScrollX, { passive: true })
            })
            ref.current.addEventListener('mouseleave', () => {
        ref.current?.removeEventListener('scroll', calculateScrollX)
            })
        }
    }, [ref, reverse, id])

    useEffect(() => {
        const currentRef = ref.current
        if (currentRef) {
            const checkNewElementAddition = () => {
                if (ref.current) {
                    const left = ref.current.scrollLeft
                    if (Math.abs(left) >= ref.current?.scrollWidth / 2) {
                        setCurrentItems([...currentItems, ...items])
                    }
                }
            }

            currentRef.addEventListener('scroll', checkNewElementAddition)
            return () => {
                currentRef.removeEventListener('scroll', checkNewElementAddition)
            }
        }
    }, [ref, currentItems, items])

    useEffect(() => {
        if (isVisible && transformRef.current) {
            const slide = [{ transform: `translate3d(${-66.6666 * (reverse ? -1 : 1)}%, 0, 0)` }]

            const timing: KeyframeAnimationOptions = {
                duration: 45000,
                delay: 1000,
                iterations: Infinity,
            }

      transformRef.current?.animate(slide, timing)
        }
    }, [isVisible, transformRef, reverse])

    return (
        <div
            className='overflow-x-auto no-scrollbar pb-4 carousel'
            dir={reverse ? 'rtl' : 'ltr'}
            ref={ref}
            onMouseDown={onMouseDown}
            id={id}
        >
            <div
                ref={transformRef}
                className='flex gap-4 m-auto transition duration-1000'
                style={{
                    transform: isVisible
                        ? `translate3d(${reverse ? boundaries.right : boundaries.left}px, 0, 0)`
                        : `translate3d(${reverse ? '-100%' : '100%'}, 0, 0)`,
                    opacity: isVisible ? 1 : 0,
                    // animation: 'slide 15s linear infinite'
                }}
            >
                {currentItems.map((field, index) => (
                    <div
                        key={`${field}-${index}`}
                        className='w-[230px] h-[90px] md:w-[295px] md:h-[135px] bg-sand flex items-center justify-center text-center p-4 flex-shrink-0'
                    >
                        <h5>{field}</h5>
                    </div>
                ))}
            </div>
        </div>
    )
}

export default Carousel
